<template>
  <div class="app-container">
    <!-- 科技感背景效果 -->
    <div class="tech-bg">
      <div class="grid"></div>
      <div class="particles">
        <div class="particle" v-for="index in 20" :key="index" :style="getParticleStyle()"></div>
      </div>
      <div class="glow-orb glow-1"></div>
      <div class="glow-orb glow-2"></div>
    </div>

    <header class="navbar" :class="{ 'menu-open': mobileMenuOpen }">
      <div class="logo">
        <img src="/favicon.ico" alt="至讯 Logo" class="logo-img" />
        <span class="logo-zh">北京至讯</span>
      </div>
      <button class="hamburger" @click="toggleMenu" :class="{ active: mobileMenuOpen }">
        <span></span>
        <span></span>
        <span></span>
      </button>
      <nav class="nav-links" v-show="mobileMenuOpen || !isMobile">
        <a href="#home" :class="{ active: activeSection === 'home' }" @click="scrollToSection($event, 'home')">首页</a>
        <a href="#about" :class="{ active: activeSection === 'about' }" @click="scrollToSection($event, 'about')">关于至讯</a>
        <a href="#products" :class="{ active: activeSection === 'products' }" @click="scrollToSection($event, 'products')">产品与服务</a>
        <a href="#cases" :class="{ active: activeSection === 'cases' }" @click="scrollToSection($event, 'cases')">客户案例</a>
        <a href="#honors" :class="{ active: activeSection === 'honors' }" @click="scrollToSection($event, 'honors')">资质荣誉</a>
        <a href="#contact" :class="{ active: activeSection === 'contact' }" @click="scrollToSection($event, 'contact')">联系我们</a>
      </nav>
      <div class="nav-actions" v-show="mobileMenuOpen || !isMobile">
        <button class="btn btn-primary" @click="showWechatModal = true">咨询顾问</button>
      </div>
    </header>
    <main>
      <HeroSection id="home" @open-modal="showWechatModal = true" />
      <FeaturesBar />
      <AboutSection id="about" @open-modal="showWechatModal = true" />
      <ProductsSection id="products" />
      <SolutionsSection v-if="false" />
      <WhyChooseUsSection />
      <ServeCustomersSection id="cases" />
      <HonorsSection id="honors" />
    </main>

    <!-- 公共底部栏 (包含 CTA 区域和页脚) -->
    <AppFooter id="contact" @open-modal="showWechatModal = true" />

    <!-- 右侧返回顶部按钮 -->
    <button 
      class="back-to-top" 
      :class="{ 'is-visible': showBackToTop }" 
      @click="scrollToTop"
      aria-label="返回顶部"
    >
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="19" x2="12" y2="5"></line>
        <polyline points="5 12 12 5 19 12"></polyline>
      </svg>
    </button>

    <!-- 微信咨询弹窗 -->
    <div class="modal-overlay" v-if="showWechatModal" @click.self="showWechatModal = false">
      <div class="modal-content">
        <button class="modal-close" @click="showWechatModal = false">&times;</button>
        <img src="./assets/link/wechat.png" alt="微信咨询" class="wechat-img" />
        <p class="wechat-text">扫码添加专属顾问微信</p>
      </div>
    </div>
  </div>
</template>

<script>
import HeroSection from './components/HeroSection.vue'
import FeaturesBar from './components/FeaturesBar.vue'
import AboutSection from './components/AboutSection.vue'
import ProductsSection from './components/ProductsSection.vue'
import SolutionsSection from './components/SolutionsSection.vue'
import WhyChooseUsSection from './components/WhyChooseUsSection.vue'
import ServeCustomersSection from './components/ServeCustomersSection.vue'
import HonorsSection from './components/HonorsSection.vue'
import AppFooter from './components/AppFooter.vue'

export default {
  name: 'App',
  data() {
    return {
      mobileMenuOpen: false,
      isMobile: false,
      activeSection: 'home',
      showBackToTop: false,
      showWechatModal: false
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    this.$nextTick(() => {
      this.initScrollAnimations()
    })
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    toggleMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMenu() {
      this.mobileMenuOpen = false
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 900
      if (!this.isMobile) {
        this.mobileMenuOpen = false
      }
    },
    scrollToSection(event, id) {
      if (event) event.preventDefault()
      this.closeMenu()
      const element = document.getElementById(id)
      if (element) {
        const offset = 100 // 导航栏高度
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - offset
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      } else if (id === 'home') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    getParticleStyle() {
      const size = Math.random() * 4 + 2
      const left = Math.random() * 100
      const delay = Math.random() * 10
      const duration = Math.random() * 10 + 15
      const opacity = Math.random() * 0.5 + 0.2
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        opacity: opacity
      }
    },
    handleScroll() {
      const navbar = this.$el.querySelector('.navbar')
      if (navbar) {
        if (window.scrollY > 50) {
          navbar.classList.add('scrolled')
        } else {
          navbar.classList.remove('scrolled')
        }
      }

      // 更新当前高亮的导航栏
      const sections = ['home', 'about', 'products', 'cases', 'honors', 'contact']
      let current = this.activeSection
      const scrollPosition = window.scrollY + 100 // 增加偏移量以便提前触发

      for (const id of sections) {
        const element = document.getElementById(id)
        if (element) {
          const top = element.getBoundingClientRect().top + window.scrollY
          if (scrollPosition >= top) {
            current = id
          }
        }
      }

      // 检查是否滚动到了底部
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50) {
        current = 'contact'
      }

      this.activeSection = current

      // 控制返回顶部按钮显示/隐藏
      this.showBackToTop = window.scrollY > 300
    },
    initScrollAnimations() {
      const targets = this.$el.querySelectorAll(
        '.section-header, .about-text, .about-stats, .product-card, .customer-card, .honor-card, .hero-content, .features-bar, .section-footer, .footer-brand, .footer-links-group, .footer-contact, .cta-content'
      )
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in')
              observer.unobserve(entry.target)
            }
          })
        }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' })
        targets.forEach((el) => {
          el.classList.add('animate-target')
          observer.observe(el)
        })
      } else {
        targets.forEach((el) => el.classList.add('animate-in'))
      }
    }
  },
  components: {
    HeroSection,
    FeaturesBar,
    AboutSection,
    ProductsSection,
    SolutionsSection,
    WhyChooseUsSection,
    ServeCustomersSection,
    HonorsSection,
    AppFooter
  }
}
</script>

<style>
/* Global Styles */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  overflow-x: hidden;
  scroll-behavior: smooth;
}

body {
  font-family: "PingFang SC", "Microsoft YaHei", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #333;
  background: #f4f8ff;
  overflow-x: hidden;
}

.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

/* Navbar */
.navbar {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%; /* 使用百分比适配不同屏幕 */
  max-width: 1400px;
  margin: 0 auto;
  height: 100px;
  background-color: transparent;
  width: 100%;
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  transition: background-color 0.3s ease, box-shadow 0.3s ease, backdrop-filter 0.3s ease;
}

/* 当导航栏滚动时，背景需要全屏宽度，但内容仍然居中 */
.navbar::before {
  content: '';
  position: absolute;
  top: 0;
  left: -50vw; /* 向左延伸一半视口宽度 */
  width: 200vw; /* 总宽度为两倍视口宽度以确保覆盖 */
  height: 100%;
  background-color: transparent;
  z-index: -1;
  transition: background-color 0.3s ease, box-shadow 0.3s ease, backdrop-filter 0.3s ease;
}

.navbar.scrolled::before {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  background-color: rgba(255, 255, 255, 0.95);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
}

.navbar.scrolled {
  /* 移除原有的背景色和阴影，交由 ::before 伪元素处理全屏背景 */
  box-shadow: none;
  background-color: transparent;
  backdrop-filter: none;
}

main {
  padding-top: 100px; /* 补偿固定导航栏占据的空间 */
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0052d9;
  flex-shrink: 0;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), text-shadow 0.3s ease;
  cursor: pointer;
}

.logo-img {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.logo:hover {
  transform: scale(1.05);
  text-shadow: 0 0 15px rgba(0, 82, 217, 0.3);
}

.logo-zh {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 2px;
}

.logo-en {
  font-size: 20px;
  font-weight: 500;
}

.nav-links {
  display: flex;
  gap: clamp(20px, 3vw, 64px); /* 使用 clamp 动态调整间距 */
}

.nav-links a {
  text-decoration: none;
  color: #333;
  font-size: 16px;
  font-weight: 400;
  position: relative;
  padding-bottom: 6px;
  transition: color 0.3s, font-weight 0.3s;
  white-space: nowrap;
}

.nav-links a::before {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #0052d9;
  border-radius: 2px;
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.nav-links a:hover::before {
  transform: scaleX(1);
  transform-origin: left;
}

.nav-links a:hover {
  color: #0052d9;
}

.nav-links a.active {
  color: #0052d9;
  font-weight: 600;
}

.nav-links a.active::before {
  display: none;
}

.nav-links a.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 3px;
  background-color: #0052d9;
  border-radius: 3px;
}

.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  z-index: 1001;
}

.hamburger span {
  display: block;
  width: 100%;
  height: 2px;
  background-color: #333;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.hamburger.active span:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.hamburger.active span:nth-child(2) {
  opacity: 0;
}

.hamburger.active span:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -5px);
}

.btn {
  padding: 10px 28px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border: none;
  outline: none;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transform: skewX(-20deg);
  transition: none;
  z-index: -1;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 82, 217, 0.3);
}

.btn:hover::after {
  animation: shine 0.7s ease-out;
}

@keyframes shine {
  100% { left: 150%; }
}

.btn-primary {
  background-color: #0052d9;
  color: #fff;
}

.btn-primary:hover {
  background-color: #0040b3;
}

@media (max-width: 1200px) {
  .navbar {
    padding: 0 24px;
  }
  .nav-links {
    gap: 20px;
  }
  .nav-links a {
    font-size: 14px;
  }
}

@media (max-width: 900px) {
  .navbar {
    flex-wrap: wrap;
    height: auto;
    padding: 16px 20px;
    gap: 0;
    background-color: rgba(255, 255, 255, 0.98);
  }

  .hamburger {
    display: flex;
  }

  .nav-links {
    order: 3;
    width: 100%;
    flex-direction: column;
    gap: 0;
    padding: 0;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease, padding 0.3s ease;
  }

  .navbar.menu-open .nav-links {
    max-height: 400px;
    padding: 16px 0 8px;
    border-top: 1px solid #eee;
  }

  .nav-links a {
    padding: 12px 0;
    font-size: 15px;
    border-bottom: 1px solid #f5f5f5;
  }

  .nav-links a.active::after {
    display: none;
  }

  .nav-actions {
    order: 2;
  }

  .navbar.menu-open .nav-actions {
    order: 3;
    width: 100%;
    padding-top: 8px;
    border-top: none;
  }

  .navbar.menu-open .nav-actions .btn {
    width: 100%;
    text-align: center;
  }
}

@media (max-width: 600px) {
  .logo-zh {
    font-size: 22px;
  }
  .logo-en {
    font-size: 16px;
  }
  .btn {
    padding: 8px 16px;
    font-size: 14px;
  }
}

/* === 全局滚动渐入动画 === */
.animate-target {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
  transition: opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94),
              transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.animate-target.animate-in {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* 为不同元素添加错开延迟 */
.product-card.animate-target:nth-child(1),
.customer-card.animate-target:nth-child(1),
.honor-card.animate-target:nth-child(1) { transition-delay: 0s; }
.product-card.animate-target:nth-child(2),
.customer-card.animate-target:nth-child(2),
.honor-card.animate-target:nth-child(2) { transition-delay: 0.08s; }
.product-card.animate-target:nth-child(3),
.customer-card.animate-target:nth-child(3),
.honor-card.animate-target:nth-child(3) { transition-delay: 0.16s; }
.product-card.animate-target:nth-child(4),
.customer-card.animate-target:nth-child(4),
.honor-card.animate-target:nth-child(4) { transition-delay: 0.24s; }
.product-card.animate-target:nth-child(5),
.honor-card.animate-target:nth-child(5) { transition-delay: 0.32s; }
.product-card.animate-target:nth-child(6),
.honor-card.animate-target:nth-child(6) { transition-delay: 0.40s; }
.honor-card.animate-target:nth-child(7) { transition-delay: 0.48s; }
.honor-card.animate-target:nth-child(8) { transition-delay: 0.56s; }
.honor-card.animate-target:nth-child(9) { transition-delay: 0.64s; }

/* 导航栏滚动增强效果 - 已经移到了上面处理全屏背景 */
/* .navbar.scrolled {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  background-color: rgba(255, 255, 255, 0.95);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
} */

/* Features bar 特殊渐入（从上方滑入） */
.features-bar.animate-target {
  transform: translateY(-20px) scale(0.98);
}
.features-bar.animate-target.animate-in {
  transform: translateY(0) scale(1);
}

/* 尊重 prefers-reduced-motion 设置 */
@media (prefers-reduced-motion: reduce) {
  .animate-target {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* === 右侧返回顶部按钮 === */
.back-to-top {
  position: fixed;
  right: 40px;
  bottom: 40px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #fff;
  color: #0052d9;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transform: translateY(20px);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.back-to-top.is-visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.back-to-top:hover {
  background-color: #0052d9;
  color: #fff;
  transform: translateY(-5px);
  box-shadow: 0 6px 16px rgba(0, 82, 217, 0.3);
}

@media (max-width: 900px) {
  .back-to-top {
    right: 20px;
    bottom: 20px;
    width: 40px;
    height: 40px;
  }
}

/* === 科技感背景动效 === */
.tech-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

/* 网格背景 */
.tech-bg .grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 82, 217, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 82, 217, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: perspective(500px) rotateX(60deg) translateY(0); }
  100% { transform: perspective(500px) rotateX(60deg) translateY(60px); }
}

/* 粒子效果 */
.tech-bg .particles {
  position: absolute;
  width: 100%;
  height: 100%;
}

.tech-bg .particle {
  position: absolute;
  background: radial-gradient(circle, rgba(0, 82, 217, 0.8) 0%, transparent 70%);
  border-radius: 50%;
  animation: float linear infinite;
}

@keyframes float {
  0% {
    transform: translateY(100vh) scale(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
    transform: translateY(90vh) scale(1);
  }
  90% {
    opacity: 1;
    transform: translateY(10vh) scale(1);
  }
  100% {
    transform: translateY(0) scale(0);
    opacity: 0;
  }
}

/* 光晕效果 */
.tech-bg .glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  animation: pulse 8s ease-in-out infinite;
}

.tech-bg .glow-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(0, 82, 217, 0.15) 0%, transparent 70%);
  top: 10%;
  right: 10%;
  animation-delay: 0s;
}

.tech-bg .glow-2 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 210, 255, 0.1) 0%, transparent 70%);
  bottom: 20%;
  left: 5%;
  animation-delay: 4s;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
}

/* 确保内容在动效之上 */
.app-container > *:not(.tech-bg):not(.navbar):not(.back-to-top):not(.modal-overlay) {
  position: relative;
  z-index: 1;
}

/* 微信咨询弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.modal-content {
  background-color: #fff;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  text-align: center;
  position: relative;
  animation: scaleUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  max-width: 90vw;
}

.modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  line-height: 1;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.modal-close:hover {
  color: #333;
  background-color: #f0f0f0;
}

.wechat-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  margin: 0 auto 20px;
}

.wechat-text {
  font-size: 16px;
  color: #333;
  font-weight: 500;
  margin: 0;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scaleUp {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* 移动端隐藏部分动效 */
@media (max-width: 768px) {
  .tech-bg .grid {
    background-size: 40px 40px;
  }
  .tech-bg .glow-orb {
    display: none;
  }
  .tech-bg .particle {
    display: none;
  }
}
</style>
